// Lightweight GTM extraction utilities with progressive enhancement for OCR/PDF/DOCX
// Dynamic imports are used to keep bundle lean; install optional deps for best results:
// pdfjs-dist, mammoth, tesseract.js

const TEXT_MAX_BYTES = 8 * 1024 * 1024; // 8MB safety cap

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

export async function extractTextFromFile(file) {
  if (!file) throw new Error('No file provided');
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();

  // Plain text
  if (type.startsWith('text/') || name.endsWith('.txt') || name.endsWith('.md')) {
    const text = await readFileAsText(file);
    return { text, meta: { method: 'text', pages: 1 } };
  }

  // DOCX via mammoth (optional)
  if (name.endsWith('.docx')) {
    try {
      const ab = await readFileAsArrayBuffer(file);
      const mammoth = await import(/* webpackIgnore: true */ 'mammoth');
      const result = await mammoth.extractRawText({ arrayBuffer: ab });
      return { text: result.value || '', meta: { method: 'docx', pages: 1 } };
    } catch (err) {
      return { text: '', meta: { method: 'docx_fallback_error', error: String(err) } };
    }
  }

  // Images via Tesseract (optional)
  if (type.startsWith('image/')) {
    try {
      const { createWorker } = await import(/* webpackIgnore: true */ 'tesseract.js');
      const worker = await createWorker();
      await worker.loadLanguage('eng');
      await worker.initialize('eng');
      const { data } = await worker.recognize(file);
      await worker.terminate();
      return { text: data?.text || '', meta: { method: 'ocr', confidence: data?.confidence } };
    } catch (err) {
      return { text: '', meta: { method: 'ocr_fallback_error', error: String(err) } };
    }
  }

  // PDF via pdfjs-dist (optional)
  if (name.endsWith('.pdf') || type === 'application/pdf') {
    try {
      const ab = await readFileAsArrayBuffer(file);
      const pdfjsLib = await import(/* webpackIgnore: true */ 'pdfjs-dist');
      // Some bundlers expose getDocument under default
      const getDocument = pdfjsLib.getDocument || pdfjsLib.default?.getDocument;
      if (!getDocument) throw new Error('pdfjs getDocument not available');
      const task = getDocument({ data: ab });
      const pdf = await task.promise;
      let text = '';
      for (let i = 1; i <= Math.min(pdf.numPages, 200); i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map(it => it.str).join(' ');
        if (text.length + pageText.length > TEXT_MAX_BYTES) break;
        text += '\n\n' + pageText;
      }
      return { text, meta: { method: 'pdf', pages: pdf.numPages } };
    } catch (err) {
      return { text: '', meta: { method: 'pdf_fallback_error', error: String(err) } };
    }
  }

  // Fallback: try as text
  try {
    const text = await readFileAsText(file);
    return { text, meta: { method: 'fallback_text' } };
  } catch (err) {
    return { text: '', meta: { method: 'unhandled', error: String(err) } };
  }
}

// Heuristic mapping of raw text to GTM fields and tasks
export function extractGTMDetails(rawText) {
  const text = (rawText || '').replace(/\r/g, '');
  const lines = text
    .split(/\n+/)
    .map(l => l.trim())
    .filter(Boolean);

  function sectionBetween(startKeywords, endKeywords) {
    const startIdx = lines.findIndex(l => startKeywords.some(k => l.toLowerCase().includes(k)));
    if (startIdx === -1) return '';
    let endIdx = lines.length;
    if (endKeywords && endKeywords.length > 0) {
      const nextIdx = lines.findIndex(
        (l, i) => i > startIdx && endKeywords.some(k => l.toLowerCase().includes(k))
      );
      if (nextIdx !== -1) endIdx = nextIdx;
    }
    return lines.slice(startIdx + 1, endIdx).join('\n');
  }

  const mappings = [
    { key: 'executiveSummary_mission', kws: ['mission'] },
    { key: 'executiveSummary_vision', kws: ['vision'] },
    { key: 'executiveSummary_valueProposition', kws: ['value proposition', 'valueprop'] },
    { key: 'targetMarket_customerNeeds', kws: ['pain points', 'customer needs'] },
    { key: 'competitiveAnalysis_competitors', kws: ['competitor', 'competition'] },
    { key: 'pricing_pricingModel', kws: ['pricing model', 'pricing strategy'] },
    { key: 'marketing_marketingChannels', kws: ['marketing channel', 'channels'] },
    { key: 'distribution_distributionChannels', kws: ['distribution channel'] },
    { key: 'sales_salesProcess', kws: ['sales process'] },
    { key: 'financial_budget', kws: ['budget'] },
    { key: 'metrics_kpis', kws: ['kpi', 'metrics'] },
  ];

  const formData = {};
  for (const map of mappings) {
    const content = sectionBetween(map.kws, []);
    if (content) formData[map.key] = content;
  }

  // Simple task extraction from roadmap / milestones looking for bullet points / dates
  const roadmap = sectionBetween(['roadmap', 'timeline', 'milestones'], []);
  const taskLines = roadmap
    .split(/\n+/)
    .map(l => l.trim())
    .filter(l => /^[-*•\d.)]/.test(l) || l.length > 0)
    .slice(0, 100);
  const tasks = taskLines
    .map(l => {
      const status = 'Not started';
      const dueMatch = l.match(/(\bq[1-4]\b|\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}\/\d{1,2}\/\d{2,4}\b)/i);
      return {
        id: crypto.randomUUID(),
        title: l.replace(/^[-*•\d.)\s]+/, ''),
        status,
        dueDate: dueMatch ? dueMatch[0] : '',
      };
    })
    .filter(t => t.title);

  // Identify issues (missing key sections) and suggestions
  const requiredKeys = [
    'executiveSummary_valueProposition',
    'targetMarket_customerNeeds',
    'competitiveAnalysis_competitors',
    'pricing_pricingModel',
    'marketing_marketingChannels',
    'distribution_distributionChannels',
    'sales_salesProcess',
    'financial_budget',
    'metrics_kpis',
  ];
  const issues = [];
  for (const k of requiredKeys) {
    if (!formData[k] || formData[k].trim().length < 15) {
      issues.push({ key: k, message: `${k.replace(/_/g, ' ')} appears missing or too brief.` });
    }
  }
  const suggestions = [];
  if (!tasks.length)
    suggestions.push({ message: 'Add clear GTM milestones with owners and dates.' });
  if (!formData['metrics_kpis'])
    suggestions.push({ message: 'Define 5-7 KPIs and tracking cadence.' });
  if (!formData['pricing_pricingModel'])
    suggestions.push({ message: 'Outline pricing tiers and discount policy.' });

  return { formData, tasks, issues, suggestions };
}

export async function fetchAndWrapFileFromUrl(url) {
  const res = await fetch(url, { mode: 'cors' }).catch(() => null);
  if (!res || !res.ok) throw new Error('Unable to fetch file from URL');
  const blob = await res.blob();
  const nameFromUrl = url.split('?')[0].split('#')[0].split('/').pop() || 'document';
  return new File([blob], nameFromUrl, { type: blob.type || 'application/octet-stream' });
}
